import React, { useState } from 'react'
import classNames from 'classnames'
import { Button } from 'react-bootstrap'
import { Tooltip } from 'antd'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import {
  faCalendar,
  faClock,
  faHourglass,
} from '@fortawesome/pro-regular-svg-icons'
import SessionsBlockIcon from 'assets/icons/SessionsBlockIcon'
import { BlockSessionContent } from 'store/states/lessons.state'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { hasProtocol } from 'helpers'
import { joinSessionBlock } from 'api/lessons/block-instances/JoinSessionBlock'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import {
  formatSessionDateLabel,
  formatSessionDurationLabel,
  formatSessionTimeLabel,
  getJoinErrorTranslationKey,
  getSessionActionLabelKey,
  getSessionActionState,
  isJoinDisabled,
  shouldShowJoinEarlyTooltip,
} from 'helpers/sessionBlockLearner.helpers'
import styles from './SessionBlock.module.scss'

interface SessionBlockProps {
  data: BlockSessionContent
}

export const SessionBlock = ({ data }: SessionBlockProps) => {
  const t = useTranslationRankmi({
    prefix: 'lessons.contentBlocks.sessionBlock',
  })
  const [sessions, setSessions] = useState(data.sessions)
  const [joiningPosition, setJoiningPosition] = useState<number | null>(null)

  const openMeetingUrl = (url: string) => {
    window.open(
      hasProtocol(url) ? url : `//${url}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleJoin = async (
    session: BlockSessionContent['sessions'][number]
  ) => {
    if (isJoinDisabled(session) || joiningPosition !== null) return

    setJoiningPosition(session.position)

    try {
      const result = await joinSessionBlock(data.id, session.position)
      setSessions((prev) =>
        prev.map((item) =>
          item.position === session.position
            ? { ...item, joinedAt: result.joinedAt }
            : item
        )
      )
      openMeetingUrl(session.meetingUrl)
    } catch (error) {
      const errorKey = getJoinErrorTranslationKey(error)
      if (errorKey === 'errors.sessionEnded') {
        setSessions((prev) =>
          prev.map((item) =>
            item.position === session.position
              ? { ...item, status: 'ended', canJoin: false }
              : item
          )
        )
      }
      ErrorToast(t(errorKey))
    } finally {
      setJoiningPosition(null)
    }
  }

  const renderActionButton = (
    session: BlockSessionContent['sessions'][number]
  ) => {
    const actionState = getSessionActionState(session)
    const disabled =
      isJoinDisabled(session) || joiningPosition === session.position
    const labelKey = getSessionActionLabelKey(actionState)

    const button = (
      <Button
        className={classNames(styles.actionButton, {
          [styles.actionButtonComingSoon]: actionState === 'comingSoonDisabled',
          [styles.actionButtonJoin]: actionState === 'join',
          [styles.actionButtonFinished]: actionState === 'finished',
        })}
        onClick={() => handleJoin(session)}
        disabled={disabled}
        data-test-id={`session-action-button-${session.position}`}
      >
        {t(labelKey)}
        {actionState === 'join' && (
          <FontAwesomeIcon className={styles.buttonArrow} icon={faArrowRight} />
        )}
      </Button>
    )

    if (shouldShowJoinEarlyTooltip(session)) {
      return (
        <Tooltip title={t('tooltips.joinEarly')} placement="top">
          <span className={styles.tooltipWrapper}>{button}</span>
        </Tooltip>
      )
    }

    return button
  }

  return (
    <div className={styles.container} data-test-id="session-block">
      {sessions.map((session) => {
        const dateLabel = formatSessionDateLabel(
          session.startDate,
          session.startTime,
          data.timezone
        )
        const timeLabel = formatSessionTimeLabel(
          session.startDate,
          session.startTime,
          data.timezone
        )
        const durationLabel = formatSessionDurationLabel(session.duration)

        return (
          <div
            key={`${data.id}-${session.position}`}
            className={styles.sessionCard}
            data-test-id={`session-card-${session.position}`}
          >
            <div className={styles.sessionHeader}>
              <h3 className={styles.sessionTitle}>{session.name}</h3>
              {session.status === 'live' && (
                <span
                  className={styles.liveBadge}
                  data-test-id="session-live-badge"
                >
                  <SessionsBlockIcon
                    tone="live"
                    size={14}
                    className={styles.liveBadgeIcon}
                  />
                  {t('liveBadge')}
                </span>
              )}
            </div>

            <div className={styles.sessionMeta}>
              <span className={styles.metaItem}>
                <FontAwesomeIcon
                  className={styles.metaIcon}
                  icon={faCalendar as IconProp}
                />
                {dateLabel}
              </span>
              <span className={styles.metaItem}>
                <FontAwesomeIcon
                  className={styles.metaIcon}
                  icon={faClock as IconProp}
                />
                {timeLabel}
              </span>
              {durationLabel && (
                <span className={styles.metaItem}>
                  <FontAwesomeIcon
                    className={styles.metaIcon}
                    icon={faHourglass as IconProp}
                  />
                  {t('durationMinutes', { count: Number(durationLabel) })}
                </span>
              )}
            </div>

            {session.description && (
              <p className={styles.sessionDescription}>{session.description}</p>
            )}

            <div className={styles.sessionFooter}>
              {renderActionButton(session)}
            </div>
          </div>
        )
      })}
    </div>
  )
}
